import React from 'react';
import { useTranslation } from 'next-i18next';
import NextLink from 'next/link';
import Button, { BUTTON_THEME, BUTTON_TYPE } from '@components/Button';

import {
  Wrapper,
  Container,
  Col1,
  Col2,
  OfferName,
  OffeExpirationDate,
  OfferDescription,
  OfferContent,
  Section,
  WYSWIGContent,
} from '@components/SingleOffer/styles';

import OfferDetailsBox from '@components/SingleOffer/OfferDetailsBox';
import routes from '@utils/routes';
import { ListDotted } from '@styles/globalStyles';

type SingleOfferProps = {
  offer: any;
  preview?: boolean;
  onBack?: () => void;
};
function SingleOffer({ onBack, offer, preview }: SingleOfferProps) {
  const { t } = useTranslation(['offers', 'languageLevels']);

  if (!offer || !Object.keys(offer).length) {
    return null;
  }

  return (
    <Wrapper className="single-offer">
      <Container>
        <Col1>
          {onBack ? (
            <Button type={BUTTON_TYPE.BUTTON} buttonTheme={BUTTON_THEME.TEXT} onClick={onBack}>
              {t('Back to offers')}
            </Button>
          ) : (
            <NextLink href={routes.offers}>
              <a href={routes.offers} className="link-back" title={t('Back to offers')}>
                {t('Back to offers')}
              </a>
            </NextLink>
          )}
          <OfferName>{offer.name}</OfferName>
          <OffeExpirationDate>
            {t('Offer expiration date')}: {offer.stopRecruitment}
          </OffeExpirationDate>
          <OfferContent>
            {offer.employerProfile ? (
              <OfferDescription dangerouslySetInnerHTML={{ __html: offer.employerProfile.description }} />
            ) : null}

            {offer.practiceOffer ? (
              <Section>
                <strong> {t('We Offer')} </strong>
                <WYSWIGContent dangerouslySetInnerHTML={{ __html: offer.practiceOffer }} />
              </Section>
            ) : null}

            <Section>
              <strong> {t('Task range')} </strong>
              <WYSWIGContent dangerouslySetInnerHTML={{ __html: offer.termsOfReference }} />
            </Section>

            {offer.skills && offer.skills.length && (
              <Section>
                <strong> {t('Skills')} </strong>
                <WYSWIGContent dangerouslySetInnerHTML={{ __html: offer.skills }} />
              </Section>
            )}
            {offer.offerSkills && offer.offerSkills.length ? (
              <Section>
                <strong> {t('Requirements')} </strong>
                {/* <p className="subtitle"> {t('We invite you if')} </p> */}

                <ListDotted dotColor="#e6007e">
                  {offer.offerSkills.map((skill) => (
                    <li key={skill.id}>{skill.name}</li>
                  ))}
                </ListDotted>
              </Section>
            ) : null}

            {offer.offerHighschoolPrefsAttributes && offer.offerHighschoolPrefsAttributes.length ? (
              <Section>
                <strong>{t('Studies')}</strong>
                <ListDotted dotColor="#e6007e">
                  {offer.offerHighschoolPrefsAttributes.map((item) => (
                    <li key={item.id}>
                      {item.highschool.name}: {item.highschoolProfile.name}
                    </li>
                  ))}
                </ListDotted>
              </Section>
            ) : null}

            {offer.offerLanguagesAttributes && offer.offerLanguagesAttributes.length ? (
              <Section>
                <strong>{t('Languages')}</strong>
                <ListDotted dotColor="#e6007e">
                  {offer.offerLanguagesAttributes.map((item) => (
                    <li key={item.id}>
                      {item.language.name}: {t(item.level, { ns: 'languageLevels' })}
                    </li>
                  ))}
                </ListDotted>
              </Section>
            ) : null}

            <Section>
              <strong> {t('Documents needed for the application')} </strong>
              {offer.requireCv && <p>CV</p>}
              {offer.requireLetter && <p>{t('Cover Letter')}</p>}
            </Section>
          </OfferContent>
        </Col1>
        <Col2>
          <OfferDetailsBox offer={offer} preview={preview} />
        </Col2>
      </Container>
    </Wrapper>
  );
}

SingleOffer.defaultProps = {
  preview: false,
  onBack: null,
};

export default SingleOffer;
